import { NumberFormatOptions, numberFormatters } from "../../shared/utils/numberFormatters";
import { BaseChartStyle } from "../../../screens/portfolio-profile/components/charts/styles/BaseChartStyle";
import { colourChartStyle } from "../../../screens/portfolio-profile/components/charts/styles/ColourChartStyle";

class ChartDataSeriesService {
    private readonly valuePrecision = 2;

    protected style: BaseChartStyle;

    constructor() {
        this.style = colourChartStyle;
    }
    
    getChartSeries = (
        underlyingPrice: number,
        intermediateStrikeStep: number,
        chartData: number[][],
    ): any[] => {
        const series: any[] = [];

        series.push(this.getInvoiceVolatilityValuesSeries(underlyingPrice, intermediateStrikeStep, chartData));
        return series;
    }
    
    private getInvoiceVolatilityValuesSeries = (
        underlyingPrice: number,
        intermediateStrikeStep: number,
        chartData: number[][],
    ) => {
        return {...
            this.getSeries("Implied Volatility", this.valuePrecision),
            markLine: this.getMarkLines(underlyingPrice, intermediateStrikeStep, chartData),
            // markArea: this.getMarkAreas(portfolioSettings),
        };
    }

    private getSeries = (name: string, valuePrecision: number) => {
        return {
            animation: false,
            name: name,
            type: 'line',
            smooth: true,
            color: this.style.impliedVolatilityLineColour,
            seriesLayoutBy: 'row',
            emphasis: { focus: 'series' },
            lineStyle: {
                type: this.style.impliedVolatilityLineType,
                width: this.style.impliedVolatilityLineWidth,
            },
            showSymbol: false,
            yAxisIndex: 0,
            tooltip: {
                valueFormatter: (value: number) => value?.toFixed(valuePrecision) ?? 'Not available'
            },
        };
    }

    private getMarkLines = (
        underlyingPrice: number,
        intermediateStrikeStep: number,
        chartData: number[][],
    ) => {
        const linesData: any[] = [];
        
        linesData.push(this.getUnderlyingPriceLine(underlyingPrice, intermediateStrikeStep, chartData));

        // if (portfolioSettings.showSupportLine) {
        //     data.push(this.getPriceLine(marketDataDto?.UnderlyingPrice!, portfolioSettings.supportLinePrice,
        //         portfolioSettings.showSupportLine, portfolioProfile, portfolioSettings));
        // }
        //
        // if (portfolioSettings.showResistanceLine) {
        //     data.push(this.getPriceLine(marketDataDto?.UnderlyingPrice!, portfolioSettings.resistanceLinePrice,
        //         portfolioSettings.showResistanceLine, portfolioProfile, portfolioSettings));
        // }
        //
        // if (deltaNeutralPriceForCurrentPortfolio !== null && portfolioSettings.showDeltaNeutralPriceLines)
        //     data.push(this.getCurrentPortfolioDeltaNeutralPriceLine(marketDataDto?.UnderlyingPrice!, portfolioProfile, deltaNeutralPriceForCurrentPortfolio, portfolioSettings));

        return {
            symbol: 'none',
            lineStyle: {
                width: 0.75,
                color: '#6E7079',
                type: 'dashed'
            },
            data: linesData,
        };
    }

    private getUnderlyingPriceLine = (
        underlyingPrice: number,
        intermediateStrikeStep: number,
        chartData: number[][],
    ): any => {
        const impliedVolatilityAtLine = this.getImpliedVolatilityAtPrice(chartData, underlyingPrice);
        
        const labelContent = impliedVolatilityAtLine !== null
            ? this.getUnderlyingLineLabelContent(impliedVolatilityAtLine, underlyingPrice)
            : null
        
        const minStrikePrice = chartData[0][0];
        
        return {
            name: labelContent,
            xAxis: (underlyingPrice - minStrikePrice) / intermediateStrikeStep,
            label: {
                show: true,
                position: 'insideEndTop',
                formatter: '{b}',
                fontWeight: this.style.styleFontWeight,
                fontSize: this.style.styleFontSize,
            },
            lineStyle: {
                type: 'solid',
                color: this.style.underlyingLineColour,
                width: '1.25',
            },
        }
    }

    private getImpliedVolatilityAtPrice = (
        chartData: number[][],
        price: number,
    ): number | null => {
        const strikes = chartData[0];
        const impliedVolatilityValues = chartData[1];

        for (let i = 0; i < strikes.length - 1; i++) {
            if (strikes[i] <= price && strikes[i + 1] > price ||
                strikes[i] >= price && strikes[i + 1] < price)
            {
                return impliedVolatilityValues[i];
            }
        }

        return null;
    }

    private getUnderlyingLineLabelContent(
        impliedVolatilityAtLine: number,
        linePrice: number,
    ): string {
        const numberFormatOptions: NumberFormatOptions = {
            useGroupFormat: false,
            alwaysUseSign: false,
        };
        
        const result = [];

        result.push(this.style.impliedVolatilitySuffix);
        result.push(`${numberFormatters.format1(impliedVolatilityAtLine, numberFormatOptions)}%`);

        result.push(`${this.style.groupsDelimiter}`);

        result.push(this.style.priceSuffix);
        result.push(numberFormatters.format0(linePrice, numberFormatOptions));

        return result.join('');
    }
}

export const chartDataSeriesService = new ChartDataSeriesService();